import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Loader2,
  Building2,
  ClipboardList,
  Megaphone,
  Image,
  Globe,
  LayoutGrid,
  Wallet,
  Ban,
  ScrollText,
  Package,
  Users,
  User,
  Vote,
  Flag,
  type LucideIcon,
} from 'lucide-react'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { AuthService } from '@/services/core/auth'
import { SearchApi, type SearchResult } from '@/services/core/searchApi'
import { useModule } from '../contexts/ModuleContext'

/** Search object types we can navigate to, with their icon and detail route. */
const TYPE_ROUTES: Record<string, { icon: LucideIcon; path: (id: string) => string }> = {
  Advertiser: { icon: Building2, path: (id) => `/advertising/advertisers/${id}` },
  Order: { icon: ClipboardList, path: (id) => `/advertising/orders/${id}` },
  LineItem: { icon: Megaphone, path: (id) => `/advertising/lineitems/${id}` },
  Creative: { icon: Image, path: (id) => `/advertising/creatives/${id}` },
  Site: { icon: Globe, path: (id) => `/publishing/sites/${id}` },
  AdUnit: { icon: LayoutGrid, path: (id) => `/publishing/ad-units/${id}` },
  EarningsAccount: { icon: Wallet, path: (id) => `/publishing/earnings/${id}` },
  Blocklist: { icon: Ban, path: (id) => `/publishing/blocklists/${id}` },
  Ruleset: { icon: ScrollText, path: (id) => `/publishing/rulesets/${id}` },
  Layout: { icon: LayoutGrid, path: (id) => `/admin/layouts/${id}` },
  AxProduct: { icon: Package, path: (id) => `/admin/products/${id}` },
  Team: { icon: Users, path: (id) => `/admin/teams/${id}` },
  Election: { icon: Vote, path: (id) => `/political/elections/${id}` },
  PoliticalEntity: { icon: Flag, path: (id) => `/political/entities/${id}` },
  PoliticalCampaign: { icon: Megaphone, path: (id) => `/political/campaigns/${id}` },
}

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

export function GlobalSearch() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentModule } = useModule()
  const hasToken = AuthService.isAuthenticated()

  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Users live under the current module's route
  const usersBase = currentModule.id === 'political' ? '/political/users' : '/admin/users'

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [term])

  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const enabled = hasToken && debounced.length >= MIN_QUERY_LENGTH

  const { data, isFetching } = useQuery({
    queryKey: ['globalSearch', debounced],
    queryFn: () => SearchApi.search({ q: debounced, pageSize: 20, orderBy: 'SCORE' }),
    enabled,
    staleTime: 30_000,
  })

  // Only keep results we know how to navigate to
  const results = useMemo(() => {
    const all = data?.searchResults ?? []
    return all.filter((r) => r.type === 'User' || !!TYPE_ROUTES[r.type]).slice(0, 10)
  }, [data])

  useEffect(() => {
    setHighlightIdx(-1)
  }, [results])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return
    const items = listRef.current.querySelectorAll('[data-search-item]')
    items[highlightIdx]?.scrollIntoView({ block: 'nearest' })
  }, [highlightIdx])

  const pathFor = (r: SearchResult) =>
    r.type === 'User' ? `${usersBase}/${r.id}` : TYPE_ROUTES[r.type].path(r.id)

  const iconFor = (r: SearchResult) => (r.type === 'User' ? User : TYPE_ROUTES[r.type].icon)

  const select = (r: SearchResult) => {
    setOpen(false)
    setTerm('')
    setDebounced('')
    navigate(pathFor(r))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setHighlightIdx((i) => (i < results.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
      setHighlightIdx((i) => (i > 0 ? i - 1 : results.length - 1))
    } else if (e.key === 'Enter' && highlightIdx >= 0 && results[highlightIdx]) {
      e.preventDefault()
      select(results[highlightIdx])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  const showMenu = open && debounced.length >= MIN_QUERY_LENGTH

  return (
    <div className="relative w-70 shrink-0" ref={ref}>
      <InputGroup>
        <InputGroupAddon>
          {isFetching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
        </InputGroupAddon>
        <InputGroupInput
          placeholder={t('appShell.searchPlaceholder')}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={showMenu}
          aria-autocomplete="list"
        />
      </InputGroup>

      {showMenu && (
        <div
          className="absolute top-full right-0 z-50 mt-1 w-96 max-w-[80vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-md"
          role="listbox"
        >
          <div className="max-h-80 overflow-y-auto p-1" ref={listRef}>
            {results.map((r, i) => {
              const ItemIcon = iconFor(r)
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  data-search-item
                  role="option"
                  aria-selected={i === highlightIdx}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${i === highlightIdx ? 'bg-accent text-accent-foreground' : ''}`}
                  onClick={() => select(r)}
                  onMouseEnter={() => setHighlightIdx(i)}
                >
                  <ItemIcon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{r.name}</span>
                    {r.summary && (
                      <span className="block truncate text-xs text-muted-foreground">{r.summary}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t(`globalSearch.types.${r.type}`, { defaultValue: r.type })}
                  </span>
                </button>
              )
            })}

            {results.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                {isFetching ? t('globalSearch.searching') : t('globalSearch.noResults')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
